/*
 * PetPax Co. – ZenMelts landing page
 * FAQ accordion, buy box (pack / purchase type / frequency), anchor CTAs.
 * Vanilla JS, no globals. Hooks are data-pplp-* attributes inside #lp-pplp.
 *
 * The buy box form is a native POST to /cart/add (return_to=/checkout).
 * This script only keeps the hidden `id` / `selling_plan` inputs and the displayed
 * prices in sync with the selection. All prices are pre-formatted by Liquid
 * (data-pplp-buybox-data) – nothing is calculated here.
 */
(function () {
  'use strict';

  var root = document.getElementById('lp-pplp');
  if (!root) return;

  var reducedMotion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;

  function each(list, fn) {
    Array.prototype.forEach.call(list, fn);
  }

  /* ---------- FAQ accordion ---------- */
  function initAccordion() {
    each(root.querySelectorAll('[data-pplp-accordion-toggle]'), function (toggle) {
      toggle.addEventListener('click', function () {
        var panel = document.getElementById(toggle.getAttribute('aria-controls'));
        if (!panel) return;
        var expanded = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', String(!expanded));
        panel.hidden = expanded;
      });
    });
  }

  /* ---------- Save bar height → scroll-margin-top of the buy box ---------- */
  function initSaveBarOffset() {
    var bar = root.querySelector('.savebar-pplp');
    if (!bar) return;

    function update() {
      root.style.setProperty('--pplp-savebar-h', bar.offsetHeight + 'px');
    }

    update();
    if ('ResizeObserver' in window) {
      new ResizeObserver(update).observe(bar);
    } else {
      window.addEventListener('resize', update);
    }
  }

  /* ---------- CTAs → buy box ---------- */
  function initAnchors() {
    var target = document.getElementById('buy-box-pplp');
    if (!target) return;
    var heading = document.getElementById('buybox-title-pplp');

    root.addEventListener('click', function (event) {
      var link = event.target.closest ? event.target.closest('a[href="#buy-box-pplp"]') : null;
      if (!link || !root.contains(link)) return;

      event.preventDefault();
      target.scrollIntoView({
        behavior: reducedMotion && reducedMotion.matches ? 'auto' : 'smooth',
        block: 'start'
      });
      if (heading) {
        try {
          heading.focus({ preventScroll: true });
        } catch (e) {
          heading.focus();
        }
      }
      if (window.history && history.replaceState) history.replaceState(null, '', '#buy-box-pplp');
    });
  }

  /* ---------- Buy box ---------- */
  function initBuyBox() {
    var box = root.querySelector('[data-pplp-buybox]');
    if (!box) return;

    var data;
    try {
      data = JSON.parse(box.querySelector('[data-pplp-buybox-data]').textContent);
    } catch (e) {
      return; // keep the server-rendered default; the native form still works
    }

    var variants = data.variants || [];
    var cards = box.querySelectorAll('[data-pplp-pack]');
    var packs = box.querySelector('.buybox__packs-pplp');
    var modeButtons = box.querySelectorAll('[data-pplp-mode]');
    var frequencyGroup = box.querySelector('[data-pplp-frequency-group]');
    var frequencyButtons = box.querySelectorAll('[data-pplp-frequency]');
    var summaryCompare = box.querySelector('[data-pplp-summary-compare]');
    var summaryPrice = box.querySelector('[data-pplp-summary-price]');
    var summaryNote = box.querySelector('[data-pplp-summary-note]');
    var planInfo = box.querySelector('[data-pplp-plan-info]');
    var disclosure = box.querySelector('[data-pplp-disclosure]');
    var form = box.querySelector('[data-pplp-cart-form]');
    var idInput = form.querySelector('[data-pplp-variant-input]');
    var planInput = form.querySelector('[data-pplp-selling-plan-input]');
    var submit = form.querySelector('[data-pplp-submit]');

    var state = {
      slot: data.defaultSlot,
      mode: data.defaultMode,
      planName: data.defaultPlanName
    };
    var submitting = false;

    // The plan for a variant: same name as the current frequency, else its first plan.
    // IDs and prices always come from that variant's own allocations.
    function planFor(variant) {
      if (!variant || !variant.plans || !variant.plans.length) return null;
      for (var i = 0; i < variant.plans.length; i++) {
        if (variant.plans[i].name === state.planName) return variant.plans[i];
      }
      return variant.plans[0];
    }

    function activePlan(variant) {
      return state.mode === 'subscribe' ? planFor(variant) : null;
    }

    function offerFor(variant) {
      return activePlan(variant) || variant.onetime;
    }

    // A pack can be bought on the current purchase type: in stock, and with a plan when subscribing.
    function isEligible(variant) {
      if (!variant || !variant.available) return false;
      return state.mode !== 'subscribe' || !!(variant.plans && variant.plans.length);
    }

    function unavailableNote(variant) {
      if (!variant.available) return packs.getAttribute('data-pplp-note-soldout');
      return packs.getAttribute('data-pplp-note-onetime-only');
    }

    // Keep a valid selection after the purchase type changes: the default pack, else the first eligible one.
    function ensureEligibleSlot() {
      if (isEligible(variants[state.slot])) return;
      if (isEligible(variants[data.defaultSlot])) {
        state.slot = data.defaultSlot;
        return;
      }
      for (var i = 0; i < variants.length; i++) {
        if (isEligible(variants[i])) {
          state.slot = i;
          return;
        }
      }
    }

    function fill(template, variant, offer) {
      return (template || '').replace('{melts}', variant.melts).replace('{save}', offer.save);
    }

    function setPrice(compareEl, priceEl, offer) {
      if (priceEl) priceEl.textContent = offer.price;
      if (compareEl) {
        compareEl.textContent = offer.compare || '';
        compareEl.hidden = !offer.compare;
      }
    }

    // Our own state is the only source of truth for the hidden inputs.
    function syncInputs() {
      var variant = variants[state.slot];
      var plan = activePlan(variant);
      idInput.value = variant ? variant.id : '';
      if (plan) {
        planInput.value = plan.id;
        planInput.disabled = false;
      } else {
        planInput.value = '';
        planInput.disabled = true; // not submitted for one-time purchases
      }
    }

    // Remove any id / selling_plan field that is not ours (e.g. injected by a subscription app widget).
    function stripForeignFields() {
      each(form.querySelectorAll('[name="selling_plan"], [name="id"]'), function (field) {
        if (field !== planInput && field !== idInput && field.parentNode) field.parentNode.removeChild(field);
      });
      if (form.id) {
        each(document.querySelectorAll('[form="' + form.id + '"]'), function (field) {
          if ((field.name === 'selling_plan' || field.name === 'id') && field !== planInput && field !== idInput) {
            field.disabled = true;
          }
        });
      }
    }

    function render() {
      var variant = variants[state.slot];
      if (!variant) return;
      var plan = activePlan(variant);
      var offer = offerFor(variant);

      each(modeButtons, function (button) {
        var active = button.getAttribute('data-pplp-mode') === state.mode;
        button.classList.toggle('is-active-pplp', active);
        button.setAttribute('aria-pressed', String(active));
      });

      each(cards, function (card) {
        var index = Number(card.getAttribute('data-pplp-pack'));
        var cardVariant = variants[index];
        if (!cardVariant) return;
        var cardOffer = offerFor(cardVariant);
        var eligible = isEligible(cardVariant);
        var selected = index === state.slot;
        var prices = card.querySelector('[data-pplp-pack-prices]');
        var note = card.querySelector('[data-pplp-pack-note]');
        var save = card.querySelector('[data-pplp-pack-save]');
        var saveValue = card.querySelector('[data-pplp-pack-save-value]');

        card.disabled = !eligible;
        card.classList.toggle('is-selected-pplp', selected);
        card.setAttribute('aria-checked', String(selected));
        card.tabIndex = selected ? 0 : -1;
        setPrice(card.querySelector('[data-pplp-pack-compare]'), card.querySelector('[data-pplp-pack-price]'), cardOffer);
        if (prices) prices.hidden = !eligible;
        if (note) {
          note.hidden = eligible;
          note.textContent = eligible ? '' : unavailableNote(cardVariant);
        }
        if (save) save.hidden = !(eligible && cardOffer.save > 0);
        if (saveValue) saveValue.textContent = cardOffer.save;
      });

      if (frequencyGroup) {
        frequencyGroup.hidden = state.mode !== 'subscribe' || !plan;
        each(frequencyButtons, function (button) {
          var name = button.getAttribute('data-pplp-frequency');
          var offered = variant.plans.some(function (p) {
            return p.name === name;
          });
          var active = !!plan && plan.name === name;
          button.hidden = !offered;
          button.classList.toggle('is-active-pplp', active);
          button.setAttribute('aria-checked', String(active));
          button.tabIndex = active ? 0 : -1;
        });
      }

      setPrice(summaryCompare, summaryPrice, offer);
      if (summaryNote) {
        var key = plan
          ? offer.save > 0 ? 'data-pplp-note-sub' : 'data-pplp-note-sub-nosave'
          : offer.save > 0 ? 'data-pplp-note-onetime-save' : 'data-pplp-note-onetime';
        summaryNote.textContent = fill(summaryNote.getAttribute(key), variant, offer);
      }
      if (planInfo) {
        planInfo.hidden = !plan;
        if (plan) planInfo.textContent = [plan.frequency ? plan.frequency + '.' : '', plan.description || ''].join(' ').trim();
      }
      if (disclosure) disclosure.hidden = !plan;

      if (!submitting) {
        var buyable = isEligible(variant);
        submit.disabled = !buyable;
        submit.textContent = submit.getAttribute(buyable ? 'data-pplp-label' : 'data-pplp-label-soldout');
      }

      syncInputs();
    }

    function selectSlot(index) {
      var variant = variants[index];
      if (!isEligible(variant)) return false;
      state.slot = index;
      render();
      return true;
    }

    each(modeButtons, function (button) {
      button.addEventListener('click', function () {
        state.mode = button.getAttribute('data-pplp-mode');
        ensureEligibleSlot();
        render();
      });
    });

    each(frequencyButtons, function (button) {
      button.addEventListener('click', function () {
        state.planName = button.getAttribute('data-pplp-frequency');
        render();
      });
    });

    each(cards, function (card) {
      card.addEventListener('click', function () {
        if (card.disabled) return;
        selectSlot(Number(card.getAttribute('data-pplp-pack')));
      });

      // radiogroup keyboard support, skipping packs that can't be bought on the current purchase type
      card.addEventListener('keydown', function (event) {
        var step = 0;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') step = 1;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') step = -1;
        if (!step) return;
        event.preventDefault();
        var index = state.slot;
        for (var i = 0; i < variants.length; i++) {
          index = (index + step + variants.length) % variants.length;
          if (selectSlot(index)) {
            cards[index].focus();
            return;
          }
        }
      });
    });

    // Capture phase on #lp-pplp: runs before any listener another script attaches to the form.
    root.addEventListener(
      'submit',
      function (event) {
        if (event.target !== form) return;
        var variant = variants[state.slot];
        if (submitting || !isEligible(variant)) {
          event.preventDefault();
          return;
        }
        stripForeignFields();
        syncInputs();
        submitting = true;
        submit.disabled = true;
        submit.setAttribute('aria-busy', 'true');
        submit.textContent = submit.getAttribute('data-pplp-label-loading');
      },
      true
    );

    // Restore the button when the page comes back from the back/forward cache.
    window.addEventListener('pageshow', function (event) {
      if (!event.persisted) return;
      submitting = false;
      submit.removeAttribute('aria-busy');
      render();
    });

    if ('MutationObserver' in window) {
      new MutationObserver(stripForeignFields).observe(form, { childList: true, subtree: true });
    }

    ensureEligibleSlot();
    render();
  }

  initAccordion();
  initSaveBarOffset();
  initAnchors();
  initBuyBox();
})();
